import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  Checkbox,
  Card,
  Modal,
  message,
  Divider
} from "antd";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchAgreements, initiateRegister } from "../../actions/auth/genesis";
import { Link, useNavigate } from "react-router-dom";
import {
  calculatePasswordScore,
  PasswordScoreClasses,
  PasswordScoreLabels
} from "../helpers/calculatePasswordScore";
import Swal from "sweetalert2";
import dayjs from "dayjs";

import utc from "dayjs/plugin/utc"; // UTC eklentisi
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import { countries } from "../../helpers/countries";
import { removeTurkish } from "../helpers/removeTurkish";

dayjs.extend(utc); // Eklentiyi aktifleştir

const { Option } = Select;

function Genesis() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    nationality: "",
    nationalIdentifier: "",
    phoneNumber: "",
    dateOfBirth: null,
    gender: 1,
    email: "",
    password: ""
  });
  const [acceptedAgreements, setAcceptedAgreements] = useState([]);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false); // Formun gönderildiğini takip eder
  const [passwordScore, setPasswordScore] = useState(0);
  const [passwordCriteriaStatus, setPasswordCriteriaStatus] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    specialChar: false
  });

  const { agreements, responseCode, responseMessage, hasError, done, spinner } =
    useSelector((state) => state.genesis);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchAgreements(formData.nationality));
  }, [dispatch, formData.nationality]);

  useEffect(() => {
    if (isSubmitted) {
      if (done && !hasError) {
        message.success(
          "Kayıt başarılı! Lütfen doğrulama işlemini tamamlayınız."
        );
        navigate("/gensis-verify");
      } else if (hasError && responseMessage && responseCode !== "PASS002") {
        Swal.fire({
          icon: "error",
          title: "Kayıt Olma Hatası",
          text: responseMessage,
          footer:
            '<a target="_blank" href="https://www.onedose.io/">Farklı bir problem yaşıyorum?</a>'
        });
      }
    }
  }, [done, hasError, responseMessage, responseCode, navigate, isSubmitted]);

  const toTitleCase = (str) => {
    return str
      .toLocaleLowerCase("tr-TR") // Önce tüm harfleri küçük yap
      .replace(/(^|\s|\.|-|,)\S/gu, (match) =>
        match.toLocaleUpperCase("tr-TR")
      ); // Kelime başındaki karakteri büyük yap
  };

  const handleInputChange = (name, value) => {
    let formattedValue = value;

    if (name === "firstName" || name === "lastName") {
      // Sayılar ve noktalama işaretleri korunarak ilk harf büyük yapılır
      formattedValue = toTitleCase(value);
    }

    setFormData({ ...formData, [name]: formattedValue });
  };

  useEffect(() => {
    setPasswordScore(calculatePasswordScore(formData.password));
  }, [formData.password]);

  const handlePhoneChange = (phone) => {
    if (!phone.startsWith("+")) {
      phone = `+${phone}`;
    }
    setFormData({ ...formData, phoneNumber: phone });
  };

  const handleAgreementChange = (agreement) => {
    if (acceptedAgreements.includes(agreement.agreementType)) {
      setAcceptedAgreements((prev) =>
        prev.filter((type) => type !== agreement.agreementType)
      );
    } else {
      setAcceptedAgreements((prev) => [...prev, agreement.agreementType]);
    }
  };

  const openAgreementModal = (agreement) => {
    setSelectedAgreement(agreement);
    setIsModalOpen(true);
  };

  useEffect(() => {
    const password = formData.password;

    setPasswordCriteriaStatus({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digit: /\d/.test(password),
      specialChar: /[!@#$%^&*]/.test(password)
    });
  }, [formData.password]);

  const handleSubmit = () => {
    const dateOfBirthISO = formData.dateOfBirth
      ? dayjs(formData.dateOfBirth) // Kullanıcının seçtiği tarih
          .add(1, "day") // 1 gün ekle
          .utc() // UTC formatına çevir
          .startOf("day") // Günü başlangıç saatine ayarla (00:00:00)
          .format() // ISO 8601 formatında döndür (örneğin: "2024-01-02T00:00:00Z")
      : null;

    const dataToSubmit = {
      ...formData,
      dateOfBirth: dateOfBirthISO,
      acceptedAgreements
    };

    setIsSubmitted(true);
    dispatch(initiateRegister(dataToSubmit));
  };

  const passwordCriteria = [
    { text: "En az 8 karakter", condition: formData.password.length >= 8 },
    {
      text: "Bir büyük harf içermeli (A-Z)",
      condition: /[A-Z]/.test(formData.password)
    },
    {
      text: "Bir küçük harf içermeli (a-z)",
      condition: /[a-z]/.test(formData.password)
    },
    {
      text: "Bir rakam içermeli (0-9)",
      condition: /\d/.test(formData.password)
    },
    {
      text: "Bir özel karakter içermeli (!@#$%^&*)",
      condition: /[!@#$%^&*]/.test(formData.password)
    }
  ];

  return (
    <div>
      <main className="flex justify-center items-center bg-gray-100">
        <Card className="shadow-xl p-4 w-full max-w-full md:max-w-[600px]">
          <div className="text-center mb-4">
            <div className="flex justify-between">
              <div>
                <h2 className="block text-lg font-bold text-gray-800 mb-5">
                  İkinci Görüş Kayıt
                </h2>
              </div>
              <div className="w-14">
                <img
                  src="https://www.onedose.io/wp-content/uploads/2020/09/onedose_black.png"
                  alt="logo"
                />
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Zaten bir hesabınız varsa{" "}
              <Link to="/login" className="text-blue-500">
                buradan giriş yapın
              </Link>
            </p>
          </div>
          <Divider />
          <Form
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={() => setIsSubmitted(false)} // Form verisi değiştiğinde "isSubmitted" sıfırlanır
          >
            <Form.Item
              label="Adınız"
              name="firstName"
              rules={[{ required: true, message: "Lütfen adınızı giriniz!" }]}
            >
              <Input
                size="large"
                value={formData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
              />
            </Form.Item>
            <Form.Item
              label="Soyadınız"
              name="lastName"
              rules={[
                { required: true, message: "Lütfen soyadınızı giriniz!" }
              ]}
            >
              <Input
                size="large"
                value={formData.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
              />
            </Form.Item>
            <Form.Item
              label="Uyruğunuz"
              name="nationality"
              rules={[
                { required: true, message: "Lütfen uyruğunuzu seçiniz!" }
              ]}
            >
              <Select
                size="large"
                showSearch
                placeholder="Uyruğunuzu seçin"
                optionFilterProp="children"
                value={formData.nationality}
                onChange={(value) => handleInputChange("nationality", value)}
                className="flex justify-start text-start items-center"
                filterOption={(input, option) =>
                  removeTurkish(
                    option?.children?.toString().toLowerCase() || ""
                  ).includes(removeTurkish(input.toLowerCase()))
                }
              >
                {countries.map((country) => (
                  <Option key={country.alpha3Code} value={country.alpha3Code}>
                    {country.turkishShortName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Kimlik Numarası"
              name="nationalIdentifier"
              rules={[
                { required: true, message: "Lütfen Kimlik Numarasını giriniz!" }
              ]}
            >
              <Input
                size="large"
                value={formData.nationalIdentifier}
                onChange={(e) =>
                  handleInputChange("nationalIdentifier", e.target.value)
                }
              />
            </Form.Item>
            <Form.Item
              label="Telefon Numarası"
              name="phoneNumber"
              rules={[
                {
                  required: true,
                  message: "Lütfen telefon numaranızı giriniz!"
                }
              ]}
            >
              <PhoneInput
                className="flex justify-start text-start items-center"
                country="tr" // Varsayılan ülke Türkiye
                value={formData.phoneNumber.replace(/^\+/, "")} // "+" sembolünü inputtan kaldır
                onChange={handlePhoneChange}
                placeholder="Telefon numaranızı girin"
              />
            </Form.Item>
            <Form.Item
              label="Doğum Tarihi"
              name="dateOfBirth"
              rules={[
                { required: true, message: "Lütfen doğum tarihinizi seçiniz!" }
              ]}
            >
              <DatePicker
                size="large"
                placeholder="Doğum gününüz"
                format="DD-MM-YYYY"
                onChange={(date) =>
                  handleInputChange(
                    "dateOfBirth",
                    date ? dayjs(date).toISOString() : null
                  )
                }
                className="w-full"
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
              />
            </Form.Item>
            <Form.Item
              label="Cinsiyet"
              name="gender"
              rules={[
                { required: true, message: "Lütfen cinsiyetinizi seçiniz!" }
              ]}
            >
              <Select
                size="large"
                className="flex justify-start text-start items-center"
                value={formData.gender}
                onChange={(value) => handleInputChange("gender", value)}
              >
                <Option className="flex justify-center items-center" value={1}>
                  Erkek
                </Option>
                <Option className="flex justify-center items-center" value={2}>
                  Kadın
                </Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="E-posta"
              name="email"
              rules={[
                {
                  required: true,
                  message: "Lütfen e-posta adresinizi giriniz!"
                },
                {
                  type: "email",
                  message: "Geçerli bir e-posta adresi giriniz!"
                },
                {
                  validator: (_, value) => {
                    const turkishChars = /[çğıöşüÇĞİÖŞÜ]/;
                    if (turkishChars.test(value)) {
                      return Promise.reject(
                        "E-posta adresi Türkçe karakter içermemelidir!(örn: ç, ğ, ı, ö, ş, ü)"
                      );
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input
                size="large"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
              />
            </Form.Item>
            <Form.Item
              label="Şifre"
              name="password"
              rules={[{ min: 8, message: "Şifre en az 8 karakter olmalıdır!" }]}
              help={
                responseCode === "PASS002" ? (
                  <span className="text-red-500">{responseMessage}</span>
                ) : null
              }
              extra={
                <div className="text-gray-500 text-sm">
                  <p>
                    Şifrenizin aşağıdaki kriterleri karşılaması gerekmektedir:
                  </p>
                  <ul className="mt-1">
                    {passwordCriteria.map((item, index) => (
                      <li key={index} className="flex items-center">
                        {item.condition ? (
                          <CheckCircleFilled className="text-green-500 mr-2" />
                        ) : (
                          <CloseCircleFilled className="text-red-500 mr-2" />
                        )}
                        {item.text}
                      </li>
                    ))}
                  </ul>
                </div>
              }
            >
              <Input.Password
                size="large"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
              />
              <span
                className={`block mt-2 font-semibold ${PasswordScoreClasses[passwordScore]}`}
              >
                {PasswordScoreLabels[passwordScore]}
              </span>
            </Form.Item>
            <ul className="mt-2 grid justify-start items-center text-center">
              {agreements.map((agreement) => (
                <li
                  key={agreement.id}
                  className="flex justify-start items-center text-center"
                >
                  <Checkbox
                    onChange={() => handleAgreementChange(agreement)}
                    checked={acceptedAgreements.includes(
                      agreement.agreementType
                    )}
                  >
                    <Button
                      type="link"
                      onClick={() => openAgreementModal(agreement)}
                    >
                      {agreement.title}
                    </Button>
                  </Checkbox>
                </li>
              ))}
            </ul>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="w-full mt-4"
                loading={spinner}
                disabled={
                  passwordScore < 5 ||
                  acceptedAgreements.length !== agreements.length ||
                  /[çğıöşüÇĞİÖŞÜ]/.test(formData.email)
                }
              >
                Doğrulama Kodu Gönder
              </Button>
            </Form.Item>
          </Form>
          <div className="flex justify-end items-end">
            <p className="text-sm text-gray-600">
              Bir sorun yaşıyorsanız{" "}
              <Link
                to="https://www.onedose.io/iletisim/"
                className="text-blue-500"
              >
                bize ulaşın
              </Link>
            </p>
          </div>
        </Card>
      </main>

      {/* Modal */}
      <Modal
        title={selectedAgreement?.title}
        open={isModalOpen}
        footer={null}
        onCancel={() => setIsModalOpen(false)}
        width={800}
      >
        {selectedAgreement && (
          <iframe
            src={selectedAgreement.contentUrl}
            title={selectedAgreement.title}
            width="100%"
            height="500px"
          />
        )}
      </Modal>
    </div>
  );
}

export default Genesis;
